<template>
  <div
    :class="['carousel', { 'carousel--peek': widePeek }]"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <div class="carousel-track col-12" ref="carousel" :style="trackStyle">
      <div
        v-for="(card, i) in cards"
        :key="i"
        :class="
          cards.length > 1
            ? {
                'carousel__slide carousel__slide--visible col-11 col-lg-4 col-md-4 col-sm-10': true,
                'carousel__slide--prev': i < currentIndex,
                'carousel__slide--active': i === currentIndex,
                'carousel__slide--next': i > currentIndex,
              }
            : {
                'carousel__slide--visible col-10 col-lg-4 col-md-4 col-sm-10 carousel__slide--active': true,
              }
        "
      >
        <slot :item="card" :index="i"></slot>
      </div>
    </div>
  </div>
  <div class="carousel-pagination" v-if="isMobile" ref="pagination">
    <span
      v-for="(card, i) in cards"
      :key="i"
      :class="['dot', { active: i === currentIndex }]"
      @click="goTo(i)"
    ></span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  cards: {
    type: Array,
    required: true,
  },
  activeIndex: {
    type: Number,
    default: 1,
  },
  // Modo "wide peek" (opt-in): no mobile o carrossel ocupa toda a largura da
  // viewport, deixando 10px de margem nas bordas e 10px de gap, exibindo um
  // trecho do(s) card(s) vizinho(s). Usado só na tela única de planos; demais
  // telas mantêm o layout padrão.
  widePeek: {
    type: Boolean,
    default: false,
  },
});

const carousel = ref<HTMLElement | null>(null);
const pagination = ref<HTMLElement | null>(null);

function clampIndex(i: number): number {
  const max = (props.cards?.length ?? 1) - 1;
  return Math.max(0, Math.min(i, max));
}

const currentIndex = ref(clampIndex(props.activeIndex));
const isMobile = ref(false);

watch(
  () => props.activeIndex,
  (newIndex) => {
    currentIndex.value = clampIndex(newIndex);
  }
);

watch(
  () => props.cards?.length ?? 0,
  (newLen, oldLen) => {
    if ((oldLen ?? 0) === 0 && newLen > 0) {
      currentIndex.value = clampIndex(props.activeIndex);
    }
    nextTick(() => equalizeCardHeights());
    setTimeout(equalizeCardHeights, 600);
  }
);

function updateIsMobile() {
  // No modo wide-peek (tela única de planos) o carrossel de 1 card vai até 970px
  // (iPads/tablets, onde o grid de 3 cards fica espremido). Demais usos: 768px.
  isMobile.value = window.innerWidth <= (props.widePeek ? 970 : 768);
}

// Modo wide-peek: iguala a altura dos cards para a rotação ficar homogênea.
// Mede a maior altura natural e aplica como min-height em todos. Só mobile;
// o conteúdo (créditos, "de/por", etc.) varia por plano e idioma, então medir
// é mais robusto do que fixar uma altura.
function equalizeCardHeights() {
  if (!props.widePeek || !isMobile.value) return;
  const track = carousel.value;
  if (!track) return;
  const cards = track.querySelectorAll<HTMLElement>(".card");
  if (cards.length < 2) return;
  cards.forEach((c) => (c.style.minHeight = ""));
  let max = 0;
  cards.forEach((c) => {
    if (c.offsetHeight > max) max = c.offsetHeight;
  });
  if (max > 0) cards.forEach((c) => (c.style.minHeight = `${max}px`));
  positionPaginationPeek();
}

// Modo wide-peek (tela de planos): os dots ficam ENTRE o card e os benefícios,
// não no rodapé do carrossel. Como todos os cards têm a mesma altura, a base do
// card cai no mesmo Y em todos os slides; medimos essa base e subimos os dots
// (que vivem fora do .carousel) com margin-top negativa. O card deixa um espaço
// reservado antes dos benefícios (margin-top em .mobile-benefits-wrapper) para
// os dots não sobreporem o texto.
function positionPaginationPeek() {
  if (!props.widePeek || !isMobile.value) return;
  const track = carousel.value;
  const pag = pagination.value;
  if (!track || !pag) return;
  const carouselEl = track.parentElement as HTMLElement | null;
  const firstCard = track.querySelector<HTMLElement>(".card");
  if (!carouselEl || !firstCard) return;
  const cardBottomY =
    firstCard.getBoundingClientRect().bottom -
    carouselEl.getBoundingClientRect().top;
  const overlap = carouselEl.offsetHeight - cardBottomY;
  // Usamos TRANSFORM (não margin negativa): o transform sobe os dots
  // visualmente para a base do card SEM encolher a altura do componente no
  // fluxo — assim o rodapé GLOBAL da página continua abaixo do carrossel, em
  // vez de ser puxado para cima e cobrir os benefícios. position/z-index
  // garantem que os dots pintem acima do .carousel (que é posicionado).
  pag.style.position = "relative";
  pag.style.zIndex = "3";
  pag.style.marginTop = "";
  pag.style.transform = `translateY(${Math.round(-overlap + 12)}px)`;
}

function onResize() {
  updateIsMobile();
  equalizeCardHeights();
}

const route = useRoute();
const storePlan = planStore();
const reactiveVoucher = computed(() => storePlan.voucher);

onMounted(() => {
  updateIsMobile();
  window.addEventListener("resize", onResize);

  if (isMobile.value) {
    window.scrollTo({
      top: 0,
    });
    viewCardPagination(
      route.query.code_share || route.name === "language-assine-turbo"
        ? 0
        : currentIndex.value
    );
  }

  // Conteúdo dos cards chega de forma assíncrona (planos do store); iguala as
  // alturas após o layout assentar (nextTick) e novamente como fallback.
  nextTick(() => equalizeCardHeights());
  setTimeout(equalizeCardHeights, 600);
  // Web fonts mudam a altura das linhas: re-iguala quando terminarem de carregar.
  if (typeof document !== "undefined" && document.fonts?.ready) {
    document.fonts.ready.then(() => equalizeCardHeights());
  }
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});

// Swipe logic
let startX = 0;
let deltaX = 0;

function onTouchStart(e: TouchEvent) {
  if (!isMobile.value) return;
  startX = e.touches[0].clientX;
}

function onTouchMove(e: TouchEvent) {
  if (!isMobile.value) return;
  deltaX = e.touches[0].clientX - startX;
}

function onTouchEnd() {
  if (!isMobile.value) return;
  if (deltaX > 50 && currentIndex.value > 0) {
    currentIndex.value--;
  } else if (deltaX < -50 && currentIndex.value < props.cards.length - 1) {
    currentIndex.value++;
  }
  deltaX = 0;
}

function goTo(i: number) {
  currentIndex.value = clampIndex(i);
}

const trackStyle = computed(() => {
  return {
    transform: "none",
    width: "100%",
    transition: "transform 0.4s cubic-bezier(.4,0,.2,1)",
    display: "flex",
  };
});

async function viewCardPagination(newIndex: number) {
  await nextTick();
  const container: HTMLElement | null = carousel.value;
  let elementFocus: string | false = false;
  const activeElements =
    document.activeElement?.getElementsByClassName?.("active");
  if (activeElements && activeElements[0]) {
    elementFocus =
      (activeElements[0 as number] as HTMLElement).innerText ?? false;
  }
  // verifica se o container está em foco
  if (
    elementFocus &&
    !["Renovar / Upgrade", "Renew / Upgrade"].some((text) =>
      elementFocus!.includes(text)
    ) &&
    route.name === "language-minhaconta"
  ) {
    return null;
  }
  if (
    container &&
    (container as HTMLElement).children &&
    (container as HTMLElement).children[newIndex]
  ) {
    const card = (container as HTMLElement).children[newIndex] as HTMLElement;
    // Centraliza o card apenas HORIZONTALMENTE, rolando o próprio container do
    // carrossel (overflow:hidden). Antes usávamos card.scrollIntoView, que também
    // rolava a JANELA na vertical (block:"nearest") e empurrava a página para o
    // topo a cada troca de slide no mobile. Rolar só o scroller pelo eixo X
    // preserva o deslize horizontal sem mexer no scroll vertical da página.
    const scroller = (container as HTMLElement).parentElement;
    if (scroller) {
      const left =
        card.offsetLeft + card.offsetWidth / 2 - scroller.clientWidth / 2;
      scroller.scrollTo({ left, behavior: "smooth" });
    }
  }
}

watch(currentIndex, async (newIndex) => {
  await viewCardPagination(newIndex);
});
</script>

<style scoped>
.carousel {
  height: 100%;
  overflow: hidden;
  width: 100%;
  overscroll-behavior: unset !important;
}

.carousel-track {
  display: flex;
  gap: var(--vc-slide-gap);
  height: 100%;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  transition: transform ease-out;
  transition-duration: var(--vc-transition-duration);
}

.carousel-card {
  flex: 0 0 100%;
  box-sizing: border-box;
  padding: 1rem;
  transition: box-shadow 0.3s;
  background: #fff;
  border-radius: 12px;
  margin: 0 0.5rem;
  min-height: 180px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.carousel-pagination {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ccc;
  margin: 0 6px;
  cursor: pointer;
  transition: background 0.3s;
  margin-bottom: 1rem;
}
.dot.active {
  background: #000;
  transform: scale(1.3);
}

/* Tema Gold (aba Renovar/Upgrade da Minha Conta): o preto do dot ativo some no
   fundo escuro. A classe .gold-theme vem da raiz do NavBarAccount, então em
   /assine a paginação continua igual. */
.gold-theme .dot.active {
  background: #e9c554;
}

.carousel__slide--active {
  position: relative;
  z-index: 2;
}

/* Desktop "genérico" dos carrosséis NÃO-peek (créditos, período, freetour): 3
   cards centralizados. O modo peek (tela de planos) é excluído via :not(...)
   porque entre 769–970 ele continua como carrossel de 1 card. */
@media (min-width: 769px) {
  .carousel:not(.carousel--peek) .carousel-track {
    transform: none !important;
    width: 100% !important;
    justify-content: center;
  }
  .carousel:not(.carousel--peek) .carousel-card {
    flex: 0 0 30%;
    margin: 0 1%;
  }
  .carousel:not(.carousel--peek) .carousel__slide--active {
    padding: 10px;
  }
}

@media (max-width: 768px) {
  .carousel:not(.carousel--peek) .carousel__slide {
    margin-inline: 0.3rem !important;
  }
}

/* Modo wide-peek (tela de planos): vai até 970px para cobrir iPads/tablets,
   onde o grid de 3 cards fica espremido. Acima de 970 o wrapper renderiza o
   grid (este componente nem é montado). */
@media (max-width: 970px) {
  /* Modo "wide peek" (tela única de planos): o carrossel sai do padding do
     .container e ocupa toda a viewport (full-bleed), deixando apenas 10px de
     margem nas bordas e 10px de gap entre os cards. O card ativo continua
     centralizado via scrollIntoView; nas pontas (Basic/Gold) o scroll fica
     preso na borda e aparece só o vizinho (Plus). Largura do card:
     100vw - margem - gap - trecho do vizinho. Ajuste fino via as 3 vars. */
  .carousel--peek {
    --peek-edge: 12px; /* margem nas bordas da viewport */
    --peek-gap: 10px; /* espaço entre os cards */
    --peek-amount: 55px; /* trecho visível do vizinho nas pontas */

    width: 100vw;
    margin-left: 50%;
    transform: translateX(-50%);
  }
  .carousel--peek .carousel-track {
    gap: var(--peek-gap);
    padding-inline: var(--peek-edge);
    /* Espaço no topo para o carimbo "Mais Escolhido" (Plus) não ser cortado
       pelo overflow do carrossel. Fica dentro da área do carrossel (visível). */
    padding-top: 10px;
  }
  .carousel--peek .carousel__slide--visible {
    flex: 0 0 auto !important;
    width: calc(
      100vw - var(--peek-edge) - var(--peek-gap) - var(--peek-amount)
    ) !important;
    margin-inline: 0 !important;
    padding-inline: 0 !important;
    /* O CSS global do vue3-carousel define `.carousel__slide { display:flex;
       align-items:center; justify-content:center }`, o que CENTRALIZA o conteúdo
       VERTICALMENTE no slide. Como cada plano tem altura total diferente
       (benefícios variam; rodapé só no Gold), essa centralização desalinha os
       topos dos cards. Aqui (modo peek = só a tela de planos) ancoramos o
       conteúdo no TOPO, mantendo os três cards alinhados. */
    flex-direction: column;
    justify-content: flex-start;
  }
}

/* Tablets/iPads (500–970px): com --peek-amount fixo em 55px, o card de 1 slide
   ocupava quase toda a largura da viewport (ex.: ~733px num tablet de 810px),
   parecendo "espichado". Aqui fixamos a largura do card em ~400px em toda a
   faixa: como a largura é `100vw - edge - gap - peek`, o peek precisa crescer
   junto com a viewport (100vw - 422px, sendo 422 = 12 edge + 10 gap + 400 card).
   Isso mantém o card compacto (~400px) e revela cada vez mais dos cards das
   pontas conforme a tela cresce, até 970px (onde o wrapper passa ao grid de 3
   cards). Ajuste fino: troque o 400 embutido no 422 pela largura desejada. */
@media (min-width: 500px) and (max-width: 970px) {
  .carousel--peek {
    --peek-amount: calc(100vw - 380px);
    --peek-gap: 20px; /* espaço entre os cards */
  }
}
</style>
